import { Info } from "@phosphor-icons/react";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "./ui/tooltip";
import { getStrengthBand } from "./NarrativeStrengthMeter";

/**
 * Stream Intelligence Panel — replaces the velocity chart.
 *
 * Surfaces 5 proprietary metrics that frame this product as
 * "cultural contradiction intelligence", not social analytics:
 *   1. Narrative Strength    (0–100, 7-band scale)
 *   2. Emotional Resonance   (0–100)
 *   3. Contradiction Density (0–100)
 *   4. Cultural Velocity     (label + new-phrase count)
 *   5. Signal Diversity      (0–100 + source count)
 *
 * Each metric is restrained: small number, hairline rule, and an ⓘ tooltip
 * with the metric's definition. No bars, no charts.
 */

function bandColor(score) {
    if (score >= 70) return "text-emerald-700";
    if (score >= 55) return "text-rose-700";
    if (score >= 40) return "text-orange-600";
    if (score >= 25) return "text-amber-700";
    if (score >= 10) return "text-slate-600";
    return "text-muted-foreground";
}

function MetricCell({ label, value, sublabel, tooltip, testid }) {
    return (
        <TooltipProvider delayDuration={150}>
            <div data-testid={testid} className="flex flex-col">
                <div className="flex items-center gap-1.5 mb-2">
                    <div className="overline text-[9px]">{label}</div>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button type="button" className="text-muted-foreground hover:text-primary">
                                <Info size={11} weight="duotone" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start" className="max-w-xs bg-card text-foreground border border-border shadow-xl">
                            <div className="text-[12px] leading-relaxed">{tooltip}</div>
                        </TooltipContent>
                    </Tooltip>
                </div>
                <div className="font-display font-bold text-3xl tabular-nums leading-none">
                    {value}
                </div>
                {sublabel && (
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-2">
                        {sublabel}
                    </div>
                )}
            </div>
        </TooltipProvider>
    );
}

export default function StreamIntelligencePanel({ metrics, velocity }) {
    if (!metrics) return null;
    const ns = metrics.narrative_strength || 0;
    const er = metrics.emotional_resonance || 0;
    const cd = metrics.contradiction_density || 0;
    const cv = metrics.cultural_velocity || {};
    const sd = metrics.signal_diversity || {};
    const band = getStrengthBand(ns);

    return (
        <section
            data-testid="stream-intelligence-panel"
            className="border border-border bg-card px-7 py-7 mb-12"
        >
            <div className="flex items-baseline gap-3 mb-6">
                <div className="overline">Intelligence panel</div>
                <div className="h-px bg-border flex-1" />
                <span className="font-mono text-[10px] text-muted-foreground tracking-wider">
                    cultural contradiction metrics
                </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-x-8 gap-y-6">
                <MetricCell
                    testid="metric-narrative-strength"
                    label="Narrative Strength"
                    value={
                        <span className={bandColor(ns)}>
                            {ns}
                        </span>
                    }
                    sublabel={band.label}
                    tooltip={
                        <>
                            How coherent and repeatable the narrative is. Built from persistence (analyses across distinct days),
                            phrase repetition, source diversity, and contradiction depth. Mapped to the 7-band scale used in Narrative Labs.
                        </>
                    }
                />
                <MetricCell
                    testid="metric-emotional-resonance"
                    label="Emotional Resonance"
                    value={<span className={bandColor(er)}>{er}</span>}
                    sublabel="emotional density"
                    tooltip={
                        <>
                            Emotional intensity and repetition. Engagement weight (likes/replies) blended with emotional-theme density per analysis.
                            High = the audience is feeling the same thing, loudly.
                        </>
                    }
                />
                <MetricCell
                    testid="metric-contradiction-density"
                    label="Contradiction Density"
                    value={<span className={bandColor(cd)}>{cd}</span>}
                    sublabel="unresolved tension"
                    tooltip={
                        <>
                            Degree of unresolved tension. Number of contradictions detected per analysis, normalized.
                            Moderate values (30–70) = real tension under the narrative, which is more interesting than calm consensus.
                        </>
                    }
                />
                <MetricCell
                    testid="metric-cultural-velocity"
                    label="Cultural Velocity"
                    value={
                        <span className="flex items-baseline gap-1.5 leading-none">
                            <span className="text-lg font-bold uppercase tracking-tight">{cv.label || "—"}</span>
                        </span>
                    }
                    sublabel={
                        cv.new_phrases_this_week > 0
                            ? `${cv.new_phrases_this_week} new phrase${cv.new_phrases_this_week === 1 ? "" : "s"} this week`
                            : "no new vocabulary"
                    }
                    tooltip={
                        <>
                            Rate at which new vocabulary is appearing week-over-week.
                            Measures the spread of *language*, not the spread of scrape volume —
                            High means new emotional vocabulary is forming inside the conversation.
                        </>
                    }
                />
                <MetricCell
                    testid="metric-signal-diversity"
                    label="Signal Diversity"
                    value={<span className={bandColor(sd.score || 0)}>{sd.score || 0}</span>}
                    sublabel={`${sd.source_count || 0} independent source${sd.source_count === 1 ? "" : "s"}`}
                    tooltip={
                        <>
                            How many independent communities/platforms are carrying this narrative.
                            Computed via Shannon entropy across (platform × community) tuples.
                            Higher = the signal is converging from many places, not one echo chamber.
                        </>
                    }
                />
            </div>
            {velocity && (
                <div className="mt-7 pt-5 border-t border-border flex items-baseline gap-x-6 gap-y-2 flex-wrap text-[12px]">
                    <span className="overline text-[9px]">Acceleration</span>
                    <span className="font-display font-semibold text-base">
                        {velocity.acceleration_label || "Steady"}
                    </span>
                    {velocity.acceleration_multiplier > 0 && (
                        <span className="font-mono text-muted-foreground">
                            {velocity.acceleration_multiplier}× narrative frequency week-over-week
                        </span>
                    )}
                    {velocity.acceleration_multiplier === 0.0 && velocity.last_3_days > 0 && (
                        <span className="font-mono text-muted-foreground">
                            narrative emerged from quiet
                        </span>
                    )}
                </div>
            )}
        </section>
    );
}
